import React from 'react';
import { StyleSheet, View } from 'react-native';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { BodyText, Button, FallbackImage } from '@sg/garnish';
import { theme } from '@garnish/constants';

import { ScanToPayEmptyStateIllustration } from '../../assets';
import {
  backgroundWithBorderPalettes,
  ScanToPayPalette,
  textPalettes,
} from '../../utils';

export const ScanToPayEmptyState = (props: ScanToPayEmptyStateProps) => {
  const { palette, imageUrl, body, onAddCard } = props;
  const { formatMessage } = useIntl();

  return (
    <View style={[styles.container, backgroundWithBorderPalettes[palette]]}>
      <FallbackImage
        baseUrl={imageUrl}
        source={ScanToPayEmptyStateIllustration}
        style={styles.illustration}
        contentfulOptions={contentfulOptions}
        aria-label={formatMessage(messages.a11yLabel)}
      />

      <View style={styles.content}>
        <BodyText
          style={[styles.body, textPalettes[palette]]}
          sizeMatch={['16']}
        >
          {body}
        </BodyText>

        <Button
          palette={palette === 'cream' ? 'spinach' : 'light'}
          size="large"
          onPress={onAddCard}
        >
          <FormattedMessage {...messages.cta} />
        </Button>
      </View>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const bannerHeight = 157;
const contentfulOptions = {
  fm: 'webp' as const,
  h: bannerHeight,
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  a11yLabel: {
    defaultMessage: 'Scan to pay empty state',
    description: 'Scan To Pay | Empty State | A11y Label',
  },
  cta: {
    defaultMessage: 'Add a card to begin',
    description: 'Scan To Pay | Empty State | CTA',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    borderRadius: theme.radius.large,
    backgroundColor: theme.colors.OPACITY.KALE.LIGHTEST,
    gap: theme.spacing['10'],
    paddingBottom: theme.spacing['10'],
  },
  content: {
    gap: theme.spacing['10'],
    paddingHorizontal: theme.spacing['8'],
  },
  body: {
    textAlign: 'center',
    color: theme.colors.KALE,
  },
  illustration: {
    width: '100%',
    height: bannerHeight,
    borderTopLeftRadius: theme.radius.large,
    borderTopRightRadius: theme.radius.large,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ScanToPayEmptyStateProps = {
  palette: ScanToPayPalette;
  imageUrl?: string;
  body: string;
  onAddCard: () => void;
};
